import { AddIcon } from "@minket/brand/icons/add";
import { SegmentsIcon } from "@minket/brand/icons/segments";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy } from "react";
import { OnDemand, Pager, usePager } from "@/components/audience/kit";
import { SegmentRegister } from "@/components/audience/segment-register";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/segments")({
  component: Segments,
});

const load = () => import("@/components/audience/segment-builder.ondemand");
const SegmentDialog = lazy(() => load().then((m) => ({ default: m.SegmentDialog })));

const columns = ["Segment and its conditions", "Contacts", "Updated"] as const;
const will = "Each segment will be listed here with its conditions and the contacts it matches.";

function Segments() {
  const { project } = useWorkspace();
  const canManage = useCan("audience.manage");
  const pending = { label: "New segment", icon: AddIcon };
  return (
    <Page
      title="Segments"
      action={
        !project ? (
          <UnavailableAction {...pending} reason="Needs a project" />
        ) : !canManage ? (
          <UnavailableAction {...pending} reason="Viewers cannot create segments" />
        ) : (
          <OnDemand
            load={load}
            button={(props) => (
              <Button {...props}>
                <AddIcon size={20} className="size-5" />
                New segment
              </Button>
            )}
            dialog={(props) => <SegmentDialog projectId={project.id} {...props} />}
          />
        )
      }
    >
      <ProjectScope
        will={will}
        icon={<SegmentsIcon />}
        preview={(busy) => <GhostTable columns={columns} label="segments" busy={busy} />}
      >
        {(current) => <SegmentList projectId={current.id} canManage={canManage} />}
      </ProjectScope>
    </Page>
  );
}

function SegmentList({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const pager = usePager();
  const query = useQuery(
    apiQuery(endpoints.segments, { projectId }, { after: pager.after, limit: 25 }),
  );
  const topics = useQuery(apiQuery(endpoints.topics, { projectId }));
  const names = new Map((topics.data?.data ?? []).map((topic) => [topic.id, topic.name]));
  const preview = (busy: boolean) => <GhostTable columns={columns} label="segments" busy={busy} />;
  return (
    <QueryView query={query} noun="segments" preview={preview}>
      {(page) =>
        page.data.length === 0 ? (
          <EmptyState
            data-state="empty"
            icon={<SegmentsIcon />}
            title="No segments yet"
            description="A segment matches contacts by their fields, properties, topics and activity, and keeps up as they change; or it holds the contacts you add to it."
            preview={preview(false)}
          />
        ) : (
          <div className="grid min-w-0 gap-3">
            <SegmentRegister
              projectId={projectId}
              segments={page.data}
              topics={names}
              canManage={canManage}
            />
            <Pager
              noun="segments"
              hasMore={page.has_more}
              pager={pager}
              lastId={page.data.at(-1)?.id}
            />
          </div>
        )
      }
    </QueryView>
  );
}
